/**
 * Turns what a player dropped into demos: a .dem as it is, or the demos inside a
 * .gz, .zst, .zip or .rar (HLTV archives hold one demo per map). Runs in the
 * import worker, so the page stays responsive while it works.
 */
import type { UnrarAnswer, UnrarRequest } from "./unrar.worker.ts";

export interface DemoFile {
  /** The demo's own name ("spirit-vs-natus-vincere-m2-dust2.dem"). */
  name: string;
  blob: Blob;
  /** When the demo file was last written (ms), when the archive says; the match's end, near enough. */
  modified?: number;
}

export type ArchiveKind = "dem" | "gzip" | "zstd" | "zip" | "rar" | "unknown";

/** What a file is, from its first bytes (names lie). */
export async function kindOf(file: Blob): Promise<ArchiveKind> {
  const head = new Uint8Array(await file.slice(0, 8).arrayBuffer());
  const ascii = String.fromCharCode(...head);
  if (ascii.startsWith("PBDEMS2")) return "dem";
  if (head[0] === 0x1f && head[1] === 0x8b) return "gzip";
  if (head[0] === 0x28 && head[1] === 0xb5 && head[2] === 0x2f && head[3] === 0xfd) return "zstd";
  if (head[0] === 0x50 && head[1] === 0x4b && head[2] === 0x03 && head[3] === 0x04) return "zip";
  if (ascii.startsWith("Rar!")) return "rar";
  return "unknown";
}

const stripExt = (name: string) => name.replace(/\.(gz|zst|zip|rar)$/i, "");
const isDem = (name: string) => /\.dem$/i.test(name);
const baseName = (path: string) => path.split(/[\\/]/).pop() ?? path;

export class UnpackError extends Error {}

/** The demos in a dropped file. `wasm.unrar` is the URL of the RAR decoder's WebAssembly file. */
export async function unpack(file: File, wasm: { unrar: string }): Promise<DemoFile[]> {
  const kind = await kindOf(file);
  switch (kind) {
    case "dem":
      return [{ name: file.name, blob: file }];
    case "gzip": {
      const out = await new Response(file.stream().pipeThrough(new DecompressionStream("gzip"))).blob();
      return checkDemos([{ name: stripExt(file.name), blob: out }]);
    }
    case "zstd": {
      // Streamed, piece by piece: FACEIT's .zst often does not say how big the demo is, which
      // the one-shot decoder needed (it failed on those), and nothing stays in WebAssembly memory.
      const { Decompress } = await import("fzstd");
      const parts: Uint8Array<ArrayBuffer>[] = [];
      const decoder = new Decompress((chunk) => parts.push(chunk as Uint8Array<ArrayBuffer>));
      const reader = file.stream().getReader();
      let pending: Uint8Array | null = null;
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        if (pending) decoder.push(pending);
        pending = value;
      }
      decoder.push(pending ?? new Uint8Array(0), true);
      return checkDemos([{ name: stripExt(file.name), blob: new Blob(parts) }]);
    }
    case "zip": {
      const { unzipSync } = await import("fflate");
      const entries = unzipSync(new Uint8Array(await file.arrayBuffer()), { filter: (f) => isDem(f.name) });
      return checkDemos(Object.entries(entries).map(([name, data]) => ({ name: baseName(name), blob: new Blob([data as Uint8Array<ArrayBuffer>]) })));
    }
    case "rar": {
      // In a worker of its own, closed once done: the decoder's memory goes with it.
      const worker = new Worker(new URL("./unrar.worker.ts", import.meta.url), { type: "module" });
      try {
        const answer = await new Promise<UnrarAnswer>((resolve, reject) => {
          worker.onmessage = (e: MessageEvent<UnrarAnswer>) => resolve(e.data);
          worker.onerror = (e) => {
            e.preventDefault();
            reject(new UnpackError("crashed"));
          };
          worker.postMessage({ file, wasm: wasm.unrar } satisfies UnrarRequest);
        });
        if (answer.type === "error") throw new UnpackError(/memory|allocat/i.test(answer.message) ? "crashed" : "unpack_failed");
        return checkDemos(answer.files.map((f) => ({ name: baseName(f.name), blob: new Blob([f.data]), modified: f.modified })));
      } finally {
        worker.terminate();
      }
    }
    default:
      throw new UnpackError("not_a_demo");
  }
}

async function checkDemos(files: DemoFile[]): Promise<DemoFile[]> {
  const demos: DemoFile[] = [];
  for (const f of files) if ((await kindOf(f.blob)) === "dem") demos.push(f);
  if (demos.length === 0) throw new UnpackError("no_demo_inside");
  return demos;
}

/**
 * A demo's fingerprint: SHA-256 of its size and its first and last 8 MB. Reading
 * the whole file would take seconds for nothing: two demos alike there are the same.
 */
export async function fingerprint(blob: Blob): Promise<string> {
  const part = 8 * 1024 * 1024;
  const head = await blob.slice(0, part).arrayBuffer();
  const tail = blob.size > part ? await blob.slice(Math.max(part, blob.size - part)).arrayBuffer() : new ArrayBuffer(0);
  const size = new TextEncoder().encode(String(blob.size));
  const all = new Uint8Array(size.length + head.byteLength + tail.byteLength);
  all.set(size, 0);
  all.set(new Uint8Array(head), size.length);
  all.set(new Uint8Array(tail), size.length + head.byteLength);
  const digest = await crypto.subtle.digest("SHA-256", all);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** gzip in the browser, for the upload. */
export async function gzip(bytes: Uint8Array): Promise<Blob> {
  return new Response(new Blob([bytes as Uint8Array<ArrayBuffer>]).stream().pipeThrough(new CompressionStream("gzip"))).blob();
}

/** And back, for the viewer. */
export async function gunzip(blob: Blob): Promise<Uint8Array> {
  return new Uint8Array(await new Response(blob.stream().pipeThrough(new DecompressionStream("gzip"))).arrayBuffer());
}
